import React, { ReactNode } from 'react';
import { Box, Typography, styled, Stack } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import {
  BORDER_COLOR,
  GRADIENT_BACKGROUND,
  GRADIENT_PRIMARY,
  GRADIENT_TEXT,
} from 'components/AI/AIStyles';
import { FeatureItem } from './components';

// Styled components
const BannerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: GRADIENT_BACKGROUND,
  border: '1px solid',
  borderColor: BORDER_COLOR,
  borderRadius: theme.shape.borderRadius * 2,
  flex: 3,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: GRADIENT_PRIMARY,
  borderRadius: '50%',
  color: theme.palette.common.white,
}));

const GradientTypography = styled(Typography)({
  backgroundImage: GRADIENT_TEXT,
  backgroundClip: 'text',
  color: 'transparent',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

// Main component props
export interface AIBannerBaseProps {
  /**
   * Title to display in the banner
   */
  title: string;
  /**
   * Description text for the banner
   */
  description: string;
  /**
   * Custom icon to display instead of the default sparkles icon
   */
  customIcon?: ReactNode;
  /**
   * Array of features to display
   */
  features?: Array<{
    id: string;
    icon: ReactNode;
    label: string;
    iconColor?: string;
  }>;
}

/**
 * AIBannerBase component to display information about AI features
 *
 * @param props - Component props
 * @returns React component
 */
export const AIBannerBase: React.FC<AIBannerBaseProps> = ({
  title,
  description,
  customIcon,
  features = [],
}) => (
  <BannerContainer>
    <HeaderContainer>
      <IconContainer>
        {customIcon || <AutoAwesome fontSize="large" />}
      </IconContainer>
      <Box>
        <GradientTypography variant="h4" fontWeight="bold">
          {title}
        </GradientTypography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </Box>
    </HeaderContainer>
    <Stack direction="row" alignItems="center" spacing={6} mt={2}>
      {features.map((feature) => (
        <FeatureItem
          key={feature.id}
          icon={feature.icon}
          label={feature.label}
          iconColor={feature.iconColor}
        />
      ))}
    </Stack>
  </BannerContainer>
);

export default AIBannerBase;
